import { haversineKm, isOnlineStatus } from "./deliverability";

const FALLBACK_CURRENCY = "GHS";
const FALLBACK_NAME = "Supplier";

export const cleanSupplierName = (name) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return FALLBACK_NAME;

  const atIndex = trimmed.indexOf("@");
  const withoutEmail = atIndex > 0 ? trimmed.slice(0, atIndex).trim() : trimmed;

  return withoutEmail || FALLBACK_NAME;
};

const numberOr = (value, fallback = null) =>
  Number.isFinite(Number(value)) && value !== null && value !== ""
    ? Number(value)
    : fallback;

export const formatMoney = (value, currency) => {
  if (!Number.isFinite(value)) return "";

  return `${currency || FALLBACK_CURRENCY} ${value.toFixed(2)}`;
};

export const formatPerKg = (value, currency) => {
  if (!Number.isFinite(value)) return "";

  return `${formatMoney(value, currency)} per Kg`;
};

export const formatDistanceKm = (distanceKm) => {
  if (!Number.isFinite(distanceKm)) return "";

  return `${distanceKm.toFixed(1)}km`;
};

export const coordinatesOf = (address) => {
  const latitude = numberOr(address?.latitude);
  const longitude = numberOr(address?.longitude);

  if (latitude === null || longitude === null) return null;

  return { latitude, longitude };
};

export const formatAddressLine = (address) =>
  [address?.address1, address?.city, address?.stateCode]
    .filter(Boolean)
    .join(", ");

const metersToKm = (meters) => {
  const parsed = numberOr(meters);

  return parsed !== null && parsed >= 0 ? parsed / 1000 : null;
};

export const mapSupplierDetail = (dto) => {
  if (!dto) return null;

  const address = dto.address || {};
  const coordinates = coordinatesOf(address);

  return {
    id: dto.id,
    name: cleanSupplierName(dto.name),
    rawName: dto.name || "",
    logoUrl: dto.referenceUrlLogo || null,
    bannerUrl: dto.bannerImageUrl || null,
    about: dto.about || dto.description || "",
    phoneNumber: dto.phoneNumber || dto.phone || null,
    email: dto.email || null,
    createdAt: dto.createdAt || null,
    online: isOnlineStatus(dto.status),
    status: dto.status || null,
    deliveryRadiusKm: metersToKm(dto.deliveryRadiusMeters),
    deliveryRadiusMeters: numberOr(dto.deliveryRadiusMeters),
    distanceKm: metersToKm(dto.distanceToLocationMeters),
    rating: numberOr(dto.avgRating, 0),
    totalRatings: numberOr(dto.totalRatings, 0),
    pricePerKg: numberOr(dto.pricePerKg?.value ?? dto.avgPricePerKg),
    currency: dto.pricePerKg?.currency || dto.avgPriceCurrency || FALLBACK_CURRENCY,
    priceFrom: numberOr(dto.priceFrom?.value),
    priceFromCurrency: dto.priceFrom?.currency || FALLBACK_CURRENCY,
    swapRefillTerms: dto.swapRefillTerms || null,
    customRefillEnabled: Boolean(dto.customRefillEnabled),
    supportedContainerOptionIds: dto.supportedContainerOptionIds || [],
    address,
    addressLine: formatAddressLine(address),
    coordinates,
    saved: dto.saved ?? null,
  };
};

const priceOf = (dto) => {
  const discounted = numberOr(dto?.discountedAmount?.value);
  const amount = numberOr(dto?.amount?.value);
  const original = numberOr(dto?.originalAmount?.value);

  const price = discounted ?? amount;
  const currency =
    dto?.discountedAmount?.currency ||
    dto?.amount?.currency ||
    dto?.originalAmount?.currency ||
    FALLBACK_CURRENCY;

  return {
    price,
    currency,
    originalPrice: discounted !== null ? original ?? amount : null,
    discounted: discounted !== null,
  };
};

export const mapProduct = (dto) => {
  if (!dto) return null;

  const { price, currency, originalPrice, discounted } = priceOf(dto);
  const volume = numberOr(dto.volume);
  const materials = dto.containerMaterials || [];

  return {
    id: dto.id,
    name: dto.name || "",
    description: dto.description || "",
    volume,
    price,
    currency,
    originalPrice,
    discounted,
    pricePerKg: price !== null && volume ? price / volume : null,
    inStock: dto.inStock !== false,
    imageUrls: dto.imageUrls || [],
    brand: dto.brand || null,
    category: dto.category || null,
    countryOfOrigin: dto.countryOfOrigin || null,
    fuelType: dto.fuelType || null,
    containerOption: dto.containerOption || null,
    containerType: dto.containerType || null,
    containerMaterials: materials,
    supplier: mapSupplierDetail(dto.supplier),
  };
};

export const mapNearbyOffer = (dto, origin) => {
  const supplier = mapSupplierDetail(dto?.supplier);
  if (!supplier?.id) return null;

  const { price, currency, originalPrice, discounted } = priceOf(dto);
  const volume = numberOr(dto.volume);

  const serverKm = metersToKm(dto.distanceToLocationMeters);
  const distanceKm =
    serverKm !== null ? serverKm : haversineKm(origin, supplier.coordinates);

  return {
    key: `nearby-${supplier.id}-${dto.id}`,
    source: "nearby",
    supplierId: supplier.id,
    productId: dto.id,
    name: supplier.name,
    logoUrl: supplier.logoUrl,
    latitude: supplier.coordinates?.latitude ?? null,
    longitude: supplier.coordinates?.longitude ?? null,
    hasCoordinates: Boolean(supplier.coordinates),
    distanceKm,
    price,
    currency,
    originalPrice,
    discounted,
    pricePerKg: price !== null && volume ? price / volume : supplier.pricePerKg,
    inStock: dto.inStock !== false,
    rating: supplier.rating,
    totalRatings: supplier.totalRatings,
    online: supplier.online,
    deliveryRadiusKm: supplier.deliveryRadiusKm,
    addressLine: supplier.addressLine,
    volume,
    imageUrls: dto.imageUrls || [],
  };
};

export const mapOriginOffer = (product, origin) => {
  const supplier = product?.supplier;
  if (!supplier?.id) return null;

  const distanceKm =
    supplier.distanceKm !== null
      ? supplier.distanceKm
      : haversineKm(origin, supplier.coordinates);

  return {
    key: `origin-${supplier.id}-${product.id}`,
    source: "origin",
    supplierId: supplier.id,
    productId: product.id,
    name: supplier.name,
    logoUrl: supplier.logoUrl,
    latitude: supplier.coordinates?.latitude ?? null,
    longitude: supplier.coordinates?.longitude ?? null,
    hasCoordinates: Boolean(supplier.coordinates),
    distanceKm,
    price: product.price,
    currency: product.currency,
    originalPrice: product.originalPrice,
    discounted: product.discounted,
    pricePerKg: product.pricePerKg ?? supplier.pricePerKg,
    inStock: product.inStock,
    rating: supplier.rating,
    totalRatings: supplier.totalRatings,
    online: supplier.online,
    deliveryRadiusKm: supplier.deliveryRadiusKm,
    addressLine: supplier.addressLine,
    volume: product.volume,
    imageUrls: product.imageUrls,
  };
};

export const mapSupplierOffer = (dto, origin) => {
  const supplierId = dto?.supplierId;
  if (supplierId === undefined || supplierId === null) return null;

  const latitude = numberOr(dto.latitude);
  const longitude = numberOr(dto.longitude);
  const coordinates =
    latitude !== null && longitude !== null ? { latitude, longitude } : null;

  const serverKm = numberOr(dto.distanceKm);
  const distanceKm =
    serverKm !== null && serverKm >= 0 ? serverKm : haversineKm(origin, coordinates);

  const price = numberOr(dto.price?.value);

  return {
    key: `offer-${supplierId}-${dto.productId}`,
    source: "offer",
    supplierId,
    productId: dto.productId ?? null,
    name: cleanSupplierName(dto.supplierName),
    logoUrl: dto.supplierLogoUrl || null,
    latitude,
    longitude,
    hasCoordinates: Boolean(coordinates),
    distanceKm,
    price,
    currency: dto.price?.currency || FALLBACK_CURRENCY,
    originalPrice: numberOr(dto.originalPrice?.value),
    discounted: Boolean(dto.discounted),
    pricePerKg: numberOr(dto.pricePerKg?.value),
    inStock: dto.inStock !== false,
    rating: numberOr(dto.avgRating, 0),
    totalRatings: numberOr(dto.totalRatings, 0),
    online: true,
    deliveryRadiusKm: null,
    addressLine: "",
    volume: null,
    imageUrls: [],
  };
};

export const mapCustomRefillQuote = (dto) => ({
  amount: numberOr(dto?.amount?.value),
  currency:
    dto?.amount?.currency || dto?.pricePerKg?.currency || FALLBACK_CURRENCY,
  refillVolume: numberOr(dto?.refillVolume),
  pricePerKg: numberOr(dto?.pricePerKg?.value),
  minRefillVolume: numberOr(dto?.minRefillVolume),
  maxRefillVolume: numberOr(dto?.maxRefillVolume),
});

export const mapAccessorySections = (dto) =>
  (dto?.sections || []).map((section) => ({
    materialCode: section.materialCode,
    title: section.materialTitle || "Accessories",
    items: (section.items || []).map(mapProduct).filter(Boolean),
  }));

export const mapReview = (dto) => ({
  id: dto?.id,
  authorName: dto?.authorName || "Customer",
  rating: numberOr(dto?.rating, 0),
  review: dto?.review || "",
  createdAt: dto?.createdAt || null,
  active: dto?.active !== false,
});

export const experienceLabel = (createdAt) => {
  if (!createdAt) return null;

  const started = new Date(createdAt);
  if (Number.isNaN(started.getTime())) return null;

  const years = Math.floor((Date.now() - started.getTime()) / 31557600000);

  if (years < 1) return "Less than a year";

  return `${years} ${years === 1 ? "year" : "years"}`;
};
